import { useGlobalWorldStatus } from "@/hooks/useGlobalWorldStatus";
import { useLanguage } from "@/hooks/useLanguage";
import { useMobileMatchMode } from "@/lib/launchHardening/mobileMatchMode";

/**
 * Lightweight global energy meter.
 * Pure CSS — no charts, no canvas. Driven by cached aggregates only.
 */
export function GlobalEnergyBar({ compact = false }: { compact?: boolean }) {
  const { t } = useLanguage();
  const s = useGlobalWorldStatus();
  const mmm = useMobileMatchMode();
  // On low-end devices, hide the energy bar entirely — let the world banner
  // carry the message without doubling overlays.
  if (mmm.profile === "low") return null;
  const pct = Math.round(s.energy * 100);
  const tensionPct = Math.round(s.tension * 100);
  const glow = (6 + s.energy * 22) * mmm.glowScale;
  const hue = 60 - s.energy * 38; // 60(amber) → 22(red)

  return (
    <div
      className="relative w-full overflow-hidden rounded-xl border border-border/60 bg-background/70 backdrop-blur"
      role="group"
      aria-label={t("gw_energy_label")}
    >
      <div className={`flex items-center gap-2 px-3 ${compact ? "py-1.5" : "py-2"}`}>
        <span aria-hidden className="text-base leading-none">🌎</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <span>{t("gw_energy_label")}</span>
            <span className="tabular-nums">{pct}%</span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-secondary/60">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${pct}%`,
                background: `linear-gradient(90deg, oklch(0.78 0.2 ${hue + 20}), oklch(0.65 0.27 ${hue}))`,
                boxShadow: `0 0 ${glow}px oklch(0.7 0.22 ${hue} / ${0.3 + s.energy * 0.5})`,
              }}
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          {!compact && (
            <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
              <span>{t("gw_tension_label")} · {tensionPct}%</span>
              {s.dominantRegion && (
                <span className="font-bold text-foreground/80">
                  {t(`gw_region_${s.dominantRegion.toLowerCase()}`)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}